import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { connect } from 'react-redux';
import { AirportSelectorDropdownEl } from './AirportSelectorDropdow.style';
import { TrackingUtils, WMPUtils, withPageContext } from '../../utils';
import { useSegmentContext } from '../FlightsManager/Segment/SegmentManager/Context';
import { EntryPoint, FieldType, LegTypeProps, SearchProp, SourceOptionsType, TabTypeProp, TabTypePropDefault } from '../../models';
import { WMPSource } from '../WMP';

export const AirportSelectorDropdown = ({
    activeProduct,
    sources,
    focus,
    hasBoxStyle,
    onClickItem,
    onClickItemSilent,
    displayedWord,
    hasSelectedValue,
    isFixedWidth,
    currentSearch,
    departureOrArrival,
    trackEvent,
}) => {
    const [sourcesToRender, setSourcesToRender] = useState(sources);
    const trackedSourcesRef = useRef(new Set());
    const trackedSourceStatesRef = useRef(new Set());
    const trackedSourceSelectionsRef = useRef(new Set());
    const segmentID = useSegmentContext().segment.id;
    const currentSegmentIndex = currentSearch.segments.findIndex(
        (segment) => segment.id === segmentID,
    );
    const fromOrTo = departureOrArrival === FieldType.DEPARTURE ? 'from' : 'to';
    const classNames = className({
        'odf-box odf-box-layer odf-popup odf-popup-flex odf-space-outer-top-xs opened': hasBoxStyle,
    });
    const isTrackableProduct =
        activeProduct === EntryPoint.FLIGHTS || activeProduct === EntryPoint.TRAINS;
    const trackingAction = isTrackableProduct
        ? TrackingUtils.ACTION[activeProduct.toUpperCase()]
        : '';
    const trackingField = departureOrArrival === FieldType.DEPARTURE ? 'orig' : 'dest';

    const ensureAutocompleteSourceExposure = useCallback((source) => {
        if (!trackingAction || trackedSourcesRef.current.has(source)) {
            return;
        }
        trackedSourcesRef.current.add(source);
        trackEvent({
            action: trackingAction,
            label: TrackingUtils.LABEL.autocompleteExposureV1({
                field: trackingField,
                source,
            }),
        });
    }, [trackEvent, trackingAction, trackingField]);

    const trackAutocompleteSourceState = useCallback(({
        source,
        state,
        queryLength = 0,
    }) => {
        const stateKey = `${source}:${state}`;
        if (!trackingAction || trackedSourceStatesRef.current.has(stateKey)) {
            return;
        }
        ensureAutocompleteSourceExposure(source);
        trackedSourceStatesRef.current.add(stateKey);
        trackEvent({
            action: trackingAction,
            label: TrackingUtils.LABEL.autocompleteListV1({
                field: trackingField,
                source,
                state,
                queryLength,
            }),
        });
    }, [ensureAutocompleteSourceExposure, trackEvent, trackingAction, trackingField]);

    const trackAutocompleteSourceSelection = useCallback(({
        source,
        position,
        queryLength = 0,
    }) => {
        if (!trackingAction || trackedSourceSelectionsRef.current.has(source)) {
            return;
        }
        trackAutocompleteSourceState({ source, state: 'shown', queryLength });
        trackedSourceSelectionsRef.current.add(source);
        trackEvent({
            action: trackingAction,
            label: TrackingUtils.LABEL.autocompleteSelectV1({
                field: trackingField,
                source,
                position,
                queryLength,
            }),
        });
    }, [trackAutocompleteSourceState, trackEvent, trackingAction, trackingField]);

    const options = {
        activeProduct,
        displayedWord,
        hasSelectedValue,
        onClickItem,
        onClickItemSilent,
        isFixedWidth,
        trackAutocompleteSourceState,
        trackAutocompleteSourceSelection,
    };

    useEffect(() => {
        if (!focus) {
            trackedSourcesRef.current.clear();
            trackedSourceStatesRef.current.clear();
            trackedSourceSelectionsRef.current.clear();
        }
    }, [focus]);

    useEffect(() => {
        const commonArgs = [currentSearch, fromOrTo, currentSegmentIndex];

        let finalSources = sources;
        if (
            WMPUtils.hasCitiesListForCurrentSegmentAndField(...commonArgs) &&
            WMPUtils.shouldWMPCitiesListShow(displayedWord, ...commonArgs)
        ) {
            finalSources = [WMPSource(...commonArgs)];
        }
        setSourcesToRender(finalSources);
    }, [currentSearch, currentSegmentIndex, displayedWord, fromOrTo, sources]);

    return (
        <>
            {focus && (
                <AirportSelectorDropdownEl className={classNames}>
                    <ul>{sourcesToRender.map((source) => source.render(options))}</ul>
                </AirportSelectorDropdownEl>
            )}
        </>
    );
};

AirportSelectorDropdown.propTypes = {
    ...SourceOptionsType,
    onClickItemSilent: PropTypes.func,
    sources: PropTypes.array.isRequired,
    focus: PropTypes.bool,
    isFixedWidth: PropTypes.bool,
    hasBoxStyle: PropTypes.bool,
    hasSelectedValue: PropTypes.bool,
    currentSearch: SearchProp.isRequired,
    departureOrArrival: LegTypeProps.isRequired,
    activeProduct: TabTypeProp,
    trackEvent: PropTypes.func,
};

AirportSelectorDropdown.defaultProps = {
    activeProduct: TabTypePropDefault,
    focus: false,
    isFixedWidth: false,
    onClickItemSilent: () => {},
    hasBoxStyle: true,
    hasSelectedValue: false,
    trackEvent: () => {},
};

const mapStateToProps = (state) => ({
    currentSearch: state.currentSearch,
});

export const ConnectedAirportSelectorDropdown = connect(
    mapStateToProps,
    {},
)(withPageContext(AirportSelectorDropdown));
